import { useAppSelector } from '../redux/hooks';
import { selectUserDetails, selectIsMultiStoreEnabled } from '../redux/UserReducer/userSlice';
import {
  DynamicUrlParams,
  DynamicBaseUrlType,
  DynamicUrlField,
  URL_PATHS,
  DEFAULT_COMPETITOR,
} from '../constants/urlPaths';
import { env } from '../env';

/**
 * Hook to get common URL parameters from Redux state
 * @returns Object containing dynamic URL parameters
 */
export const useDynamicUrlParams = (): DynamicUrlParams => {
  const userDetails = useAppSelector(selectUserDetails);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  const viewMode = isMultiStoreEnabled ? 'M' : 'S';

  return {
    storeNumber: userDetails?.currentStoreNumber || '',
    viewMode: viewMode,
    isMultiStore: isMultiStoreEnabled ? 'Y' : 'N',
    ibToken: userDetails?.ibToken || '',
    competitor: DEFAULT_COMPETITOR,
  };
};

/**
 * Hook that returns a function to build dynamic URLs
 * Combines base URL (WFC or ACENET), path template, and parameters
 *
 * @returns Function that takes baseUrlType, urlCode, and optional field, returns complete URL
 *
 * @example
 * const buildDynamicUrl = useBuildDynamicUrl();
 * const url = buildDynamicUrl('WFC', 'MGIS'); // Auto-detects field if only one exists
 * const url = buildDynamicUrl('WFC', 'CRPC', 'description'); // Explicit field when multiple exist
 */
export const useBuildDynamicUrl = (): ((
  baseUrlType: DynamicBaseUrlType,
  urlCode: string,
  field?: string,
) => string | null) => {
  const params = useDynamicUrlParams();
  return (
    baseUrlType: DynamicBaseUrlType,
    urlCode: string,
    field?: DynamicUrlField,
  ): string | null => {
    const urlPaths = URL_PATHS[urlCode];
    if (!urlPaths) {
      return null;
    }

    // Auto-detect field if not provided - use first available field
    let pathTemplate: string | undefined;
    if (field) {
      pathTemplate = urlPaths[field];
    } else {
      const firstKey = Object.keys(urlPaths)[0] as DynamicUrlField | undefined;
      pathTemplate = firstKey ? urlPaths[firstKey] : undefined;
    }
    if (!pathTemplate) {
      return null;
    }
    // Get base URL from env based on type
    let baseUrl = '';
    if (baseUrlType === 'WFC') {
      baseUrl = env.BASE_URL_WFC || '';
    } else if (baseUrlType === 'WFC2') {
      baseUrl = env.BASE_URL_WFC2 || '';
    } else if (baseUrlType === 'ACENET') {
      baseUrl = env.BASE_URL_ACENET || '';
    } else if (baseUrlType === 'RETAILER') {
      baseUrl = env.BASE_URL_RETAILER || '';
    }

    // If base URL is missing or empty, fail fast instead of returning a relative URL
    if (!baseUrl) {
      console.error(`Missing base URL configuration for type "${baseUrlType}". `);
      return null;
    }

    // Replace all {placeholder} with corresponding param values
    const resolvedPath = pathTemplate.replace(/\{(\w+)\}/g, (match, key) => {
      const value = params[key];
      if (value === undefined) {
        return match; // Keep placeholder if no value provided
      }
      // ibToken is already URL-encoded; encoding it again would double-encode % characters
      if (key === 'ibToken') {
        return value;
      }
      return encodeURIComponent(value);
    });
    return `${baseUrl}${resolvedPath}`;
  };
};
